import {
  Grid,
  Stack,
  Typography,
  TextField,
  Divider,
  RadioGroup,
  FormControlLabel,
  Radio,
  Tooltip,
} from "@mui/material";
import { InputControl } from "../../../shared";
import {
  useStockTagQueries,
  useStockCategoryQueries,
} from "../../StockCategoriesAndTags/hooks";
import { useMemo } from "react";

export default function StockTakeSection({
  data,
  onChange,
  validator,
  buyingUnits,
  alternateNameDisabled,
  disabled = false,
}) {
  const { listQuery: tagListQuery } = useStockTagQueries();
  const { listQuery: categoryListQuery } = useStockCategoryQueries();

  const isRequired = !!(
    data?.stock_category_id ||
    data?.stock_subcategory_id ||
    data?.stock_unit_id
  );

  const optionsStockItemTag =
    tagListQuery?.data?.map((item) => ({
      label: item.name,
      value: item.id,
    })) ?? [];

  const optionsStockCategory =
    categoryListQuery?.data?.map((item) => ({
      label: item.name,
      value: item.id,
      subData: item.subData || [],
    })) ?? [];

  // Get selected category to find its subcategories
  const selectedCategory = optionsStockCategory.find(
    (cat) => cat.value === data?.stock_category_id,
  );

  const optionsStockSubCategory =
    selectedCategory?.subData?.map((item) => ({
      label: item.name,
      value: item.id,
    })) ?? [];

  // Filter out "gm" from stock units
  const stockUnits = buyingUnits?.filter(
    (unit) => unit?.name?.toLowerCase() !== "gm",
  );

  const referenceUnitOptions = stockUnits?.filter(
    (unit) => unit.id !== data?.stock_unit_id,
  );

  // Determine the selected reference unit object based on stock_alt_unit_id
  const referenceUnit = useMemo(() => {
    return (
      (referenceUnitOptions?.length > 0 &&
        data?.stock_alt_unit_id &&
        referenceUnitOptions?.find((c) => c.id === data?.stock_alt_unit_id)) ||
      null
    );
  }, [data?.stock_alt_unit_id, referenceUnitOptions]);

  return (
    <Grid
      container
      gap="24px"
      p="24px"
      borderBottom="1px solid"
      borderColor="divider"
      sx={{ backgroundColor: "background.paper" }}
    >
      <Grid item xs={12} sm={4}>
        <Typography
          variant="subtitle1"
          fontWeight={600}
          sx={{ width: "150px" }}
        >
          Stocktake
        </Typography>
        <Typography variant="subtitle2" color="#657083">
          Add stocktake information
        </Typography>
      </Grid>
      <Grid item flex={1}>
        <Stack
          gap="15px"
          flexDirection="row"
          flexWrap={{ md: "nowrap", xs: "wrap" }}
        >
          <InputControl
            name="stock_category_id"
            label="Stock Category"
            fullWidth
            type="dropdown"
            disabled={disabled}
            options={optionsStockCategory ?? []}
            getOptionLabel={(option) => option?.label}
            value={
              (optionsStockCategory?.length > 0 &&
                data?.stock_category_id &&
                optionsStockCategory?.find(
                  (c) => c.value === data?.stock_category_id,
                )) ||
              null
            }
            onChange={(e, newValue) => {
              onChange({
                target: {
                  name: "stock_category_id",
                  value: newValue?.value,
                },
              });
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Stock Category"
                required={isRequired}
                error={
                  isRequired
                    ? validator.current.message(
                        "Stock Category",
                        data?.stock_category_id,
                        "required",
                      )
                    : false
                }
                helperText={
                  isRequired
                    ? validator.current.message(
                        "Stock Category",
                        data?.stock_category_id,
                        "required",
                      )
                    : undefined
                }
              />
            )}
          />

          <InputControl
            name="stock_subcategory_id"
            label="Stock Sub Category"
            fullWidth
            type="dropdown"
            disabled={disabled || !data?.stock_category_id}
            options={optionsStockSubCategory ?? []}
            getOptionLabel={(option) => option?.label}
            value={
              (optionsStockSubCategory?.length > 0 &&
                data?.stock_subcategory_id &&
                optionsStockSubCategory?.find(
                  (c) => c.value === data?.stock_subcategory_id,
                )) ||
              null
            }
            onChange={(e, newValue) => {
              onChange({
                target: {
                  name: "stock_subcategory_id",
                  value: newValue?.value,
                },
              });
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Stock Sub Category"
                required={isRequired}
                error={
                  isRequired
                    ? validator.current.message(
                        "Stock Sub Category",
                        data?.stock_subcategory_id,
                        "required",
                      )
                    : false
                }
                helperText={
                  isRequired
                    ? validator.current.message(
                        "Stock Sub Category",
                        data?.stock_subcategory_id,
                        "required",
                      )
                    : undefined
                }
              />
            )}
          />
          <InputControl
            name="stock_unit_id"
            label="Stock Unit"
            fullWidth
            type="dropdown"
            disabled={disabled}
            options={stockUnits ?? []}
            getOptionLabel={(option) => option?.name}
            value={
              (stockUnits?.length > 0 &&
                data?.stock_unit_id &&
                stockUnits?.find((c) => c.id === data?.stock_unit_id)) ||
              null
            }
            onChange={(e, newValue) => {
              onChange({
                target: {
                  name: "stock_unit_id",
                  value: newValue?.id,
                },
              });
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Stock Unit"
                required={isRequired}
                error={
                  isRequired
                    ? validator.current.message(
                        "Stock Unit",
                        data?.stock_unit_id,
                        "required",
                      )
                    : false
                }
                helperText={
                  isRequired
                    ? validator.current.message(
                        "Stock Unit",
                        data?.stock_unit_id,
                        "required",
                      )
                    : undefined
                }
              />
            )}
          />
        </Stack>
        <Stack
          gap="15px"
          mt="15px"
          flexDirection="row"
          flexWrap={{ md: "nowrap", xs: "wrap" }}
        >
          <InputControl
            name="stock_reference_unit_id"
            label="Reference Unit"
            fullWidth
            type="dropdown"
            disabled={disabled}
            options={referenceUnitOptions ?? []}
            getOptionLabel={(option) => option?.name}
            value={referenceUnit}
            onChange={(e, newValue) => {
              onChange({
                target: {
                  name: "stock_alt_unit_id",
                  value: newValue?.id,
                },
              });
            }}
            renderInput={(params) => (
              <TextField {...params} label="Reference Unit" />
            )}
          />
          <InputControl
            name="stock_code"
            label="Stock Code"
            fullWidth
            value={data?.stock_code}
            onChange={onChange}
            disabled={disabled}
            autoComplete="off"
          />

          <InputControl
            name="stock_tag_id"
            label="Stock Item Tag"
            fullWidth
            type="dropdown"
            disabled={disabled}
            options={optionsStockItemTag ?? []}
            getOptionLabel={(option) => option?.label}
            value={
              (optionsStockItemTag?.length > 0 &&
                data?.stock_tag_id &&
                optionsStockItemTag?.find(
                  (c) => c.value === data?.stock_tag_id,
                )) ||
              null
            }
            onChange={(e, newValue) => {
              onChange({
                target: {
                  name: "stock_tag_id",
                  value: newValue?.value,
                },
              });
            }}
            renderInput={(params) => (
              <TextField {...params} label="Stock Item Tag" />
            )}
          />
        </Stack>

        <Divider sx={{ my: "16px" }} />

        <Stack direction="row" alignItems="center">
          <Typography variant="subtitle1" fontWeight={500}>
            Stocktake Display name*
          </Typography>
          <RadioGroup
            row
            name="stocktake_display_name"
            value={data?.stocktake_display_name ?? "ingredient_name"}
            onChange={onChange}
            sx={{ ml: "auto" }}
          >
            <FormControlLabel
              value="ingredient_name"
              control={<Radio size="small" />}
              label="Ingredient name"
              disabled={disabled}
            />
            {
              <Tooltip
                title={
                  alternateNameDisabled ? "Alternate name is not set." : ""
                }
                arrow
              >
                <FormControlLabel
                  value="alternate_name"
                  control={<Radio size="small" />}
                  label="Alternate name"
                  disabled={disabled || alternateNameDisabled}
                />
              </Tooltip>
            }
          </RadioGroup>
        </Stack>
      </Grid>
    </Grid>
  );
}
